import React, { memo, useCallback, useEffect, useRef } from 'react'
import './Vocabulary.less'
import { DragDropContext, Draggable, DraggableProvided, DroppableProvided, DropResult } from 'react-beautiful-dnd'
import StrictModeDroppable from './StrictModeDroppable'
import { getCurrentRow, setFocus } from '../Store/VocabulariesSlice'
import { IVocabulary } from '../Types/VocabularyTypes'
import { DisplayTool } from './VocabularyTools'
import VocabularyRow from './VocabularyRow'
import { placeCaret, Position } from '../Utilities/tools'
import { useAppDispatch, useAppSelector } from '../hooks'
import { useVocabularyActions } from '../hooks/useVocabularyActions'

interface VocabularyProps {
  vocabulary: IVocabulary
  displayTools?: DisplayTool
  readOnly?: boolean
}

const Vocabulary = ({ vocabulary, displayTools = '', readOnly = false }: VocabularyProps) => {
    const dispatch = useAppDispatch()
    const vocabularyRef = useRef<HTMLDivElement>(null)

    const currentRow = useAppSelector(getCurrentRow)
    const highlightedRows = useAppSelector(state => state.vocabularies.highlightedRows)
    const { handleVocabularyChange } = useVocabularyActions(vocabulary, currentRow)

    // Scroll to first highlighted row when highlights change
    useEffect(() => {
      if (highlightedRows.length > 0 && vocabularyRef.current) {
        const firstHighlightedIndex = Math.min(...highlightedRows)
        const rows = vocabularyRef.current.querySelectorAll('.table .tr')
        if (firstHighlightedIndex < rows.length) {
          const targetRow = rows[firstHighlightedIndex] as HTMLElement
          targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }, [highlightedRows])

    const handleFocus = useCallback(
      (index: number, column: 'left' | 'right') => {
        console.log('handleFocus', index, column)
        // Check if we need to add a new row when focusing the last row
        if (index === vocabulary.rows.length - 1) {
          handleVocabularyChange({ operation: 'addRow', position: vocabulary.rows.length - 1 })
        }

        dispatch(setFocus({ index, column }))
      },
      [vocabulary.rows.length, handleVocabularyChange, dispatch]
    )

    const handleDragEnd = useCallback(
      (result: DropResult) => {
        if (!result.destination) return

        const items = Array.from(vocabulary.rows)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)

        handleVocabularyChange({
            operation: 'rearrangeRows',
            rows: items,
          })
      },
      [vocabulary.rows, handleVocabularyChange]
    )

    const navigateToRow = useCallback((targetIndex: number, columnClass: string, caretPos: Position) => {
      console.log('navigateToRow', targetIndex, columnClass)
      if (targetIndex < 0 || targetIndex >= vocabulary.rows.length) {
        console.warn('not enough rows', targetIndex, columnClass)
        return
      }
      if (!vocabularyRef.current) {
        console.log('vocabulary element not found', vocabularyRef.current)
        return
      }

      const rows = vocabularyRef.current.querySelectorAll('.table .tr')
      if (targetIndex < rows.length) {
        const targetRow = rows[targetIndex]
        const targetCell = targetRow.querySelector(`.td.${columnClass === 'left' ? 'left' : 'right'} .input`) as HTMLElement
        if (targetCell) {
          placeCaret(targetCell, caretPos)
        }
      }
    }, [vocabulary.rows.length])

    return (
      <div className={`Vocabulary ${displayTools ? 'show-tools' : ''} ${readOnly ? 'read-only' : 'editable'}`}
           ref={vocabularyRef}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <StrictModeDroppable droppableId="table">
            {(provided: DroppableProvided) => (
              <div className="table" {...provided.droppableProps} ref={provided.innerRef}>
                {vocabulary.rows.map((row, index) => {
                  return (
                    <Draggable key={row.localId} draggableId={row.localId.toString()} index={index}>
                      {(provided: DraggableProvided, snapshot) => (
                        <VocabularyRow
                          row={row}
                          index={index}
                          onFocus={handleFocus}
                          vocabulary={vocabulary}
                          readOnly={readOnly}
                          displayTools={displayTools}
                          provided={provided}
                          isDragging={snapshot.isDragging}
                          navigateToRow={navigateToRow}
                        />
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      </div>
    )
  }

export default memo(Vocabulary)